import React, { Suspense, useState, useEffect, useCallback } from 'react';

import DatePicker from 'react-datepicker';
import { useParams, useSearchParams } from 'react-router-dom';

// UI Components
import Card from 'components/Card/Card';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import TableReports from 'components/Tables/SingleMember/TableSingleMember';
import UserOverview from 'components/UserOverview/UserOverview';
import WorkCalendar from 'components/WorkCalendar/WorkCalendar';
import { ProjectSelectExternalState } from 'components/Form/Selects/ProjectsSelect';
import SkeletonTable from 'components/LoadingSkeletons/SkeletonTable/SkeletonTable';
import SkeletonLoadingField from 'components/LoadingSkeletons/SkeletonLoadingField/SkeletonLoadingField';

import parseYearMonthDate, {
  parseMonthsToNameFromDate,
} from 'utils/dateFunctions';

// styles
import styles from './SingleMember.module.scss';
import sharedStyles from 'styles/shared.module.scss';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';

// data
import instance from 'axiosInstance';
import { useQuery } from 'react-query';

const getUserCard = async ({ queryKey }) => {
  const response = await instance.get(
    '/private/admin/employee-card?slug=' + queryKey[1]
  );

  return response.data;
};

const requestCSV = async ({ queryKey }) => {
  const response = await instance.get(
    `/private/admin/employee-exports/${queryKey[4]}?start_date=${
      queryKey[1]
    }&end_date=${queryKey[2]}${queryKey[3] ? `&project_id=${queryKey[3]}` : ''}`
  );

  return response.data;
};

const TabNewSingleMember = () => {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params or defaults
  const getInitialStartDate = () => {
    const param = searchParams.get('startDate');
    if (param) return new Date(param);
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
  };

  const getInitialEndDate = () => {
    const param = searchParams.get('endDate');
    return param ? new Date(param) : new Date();
  };

  const getInitialProject = () => {
    const projectId = searchParams.get('projectId');
    const projectLabel = searchParams.get('projectLabel');
    return projectId
      ? { value: Number(projectId), label: projectLabel || projectId }
      : null;
  };

  const [startDate, setStartDate] = useState(getInitialStartDate);
  const [endDate, setEndDate] = useState(getInitialEndDate);
  const [project, setProject] = useState(getInitialProject);

  // Sync state changes to URL
  const updateUrlParams = useCallback(
    (newStartDate, newEndDate, newProject) => {
      const params = new URLSearchParams();
      params.set('startDate', parseYearMonthDate(newStartDate));
      params.set('endDate', parseYearMonthDate(newEndDate));
      if (newProject) {
        params.set('projectId', newProject.value);
        params.set('projectLabel', newProject.label);
      }
      setSearchParams(params, { replace: true });
    },
    [setSearchParams]
  );

  // Update URL when filters change
  useEffect(() => {
    updateUrlParams(startDate, endDate, project);
  }, [startDate, endDate, project, updateUrlParams]);

  const { data, isLoading } = useQuery(['user-card', slug], getUserCard, {
    suspense: false,
    retry: false,
  });

  const { refetch } = useQuery(
    [
      'user-csv',
      parseYearMonthDate(startDate),
      parseYearMonthDate(endDate),
      project?.value,
      slug,
    ],
    requestCSV,
    {
      enabled: false,
      suspense: false,
      onSuccess: (response) => {
        const fileName = `${slug}'s report -${startDate}-${endDate}`;
        const blob = new Blob([response], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('href', url);
        a.setAttribute('download', fileName + '.csv');
        a.click();
      },
    }
  );
  return (
    <>
      <div className={styles['member-information-wrapper']}>
        <Card additionalStyles={styles['member-information-box']}>
          <ErrorBoundary>
            {!isLoading && <UserOverview data={data} />}
          </ErrorBoundary>
        </Card>
        <Card
          hasPadding
          additionalStyles={styles['member-information-reports']}
        >
          <FlexContainer align="align-center" justify="justify-between">
            <span className={styles['filter-title']}>Filter reports by:</span>
            <label className={styles['select-label']}>
              Project
              <ErrorBoundary>
                <Suspense fallback={<SkeletonLoadingField />}>
                  <ProjectSelectExternalState
                    value={project}
                    setterFunction={setProject}
                  />
                </Suspense>
              </ErrorBoundary>
            </label>

            <label className={styles['date-picker-label']}>
              Starting Date
              <DatePicker
                selected={new Date(startDate)}
                onChange={(date) => {
                  if (date <= endDate) {
                    setStartDate(date);
                  }
                }}
              />
            </label>
            <label className={styles['date-picker-label']}>
              Ending date
              <DatePicker
                selected={new Date(endDate)}
                onChange={(date) => {
                  if (date >= startDate) {
                    setEndDate(date);
                  }
                }}
              />
            </label>
          </FlexContainer>
        </Card>

        <div className={styles['member-data-calendar']}>
          <Card
            title="Work recorded"
            hasPadding
            additionalStyles={styles['member-data-calendar-inner']}
          >
            <ErrorBoundary>
              <Suspense fallback={<div>Loading</div>}>
                <WorkCalendar />
              </Suspense>
            </ErrorBoundary>
          </Card>
        </div>
      </div>
      <div className={styles['member-table-wrapper']}>
        <Card
          title={`${data?.employee.name}’s reports for the period of`}
          titleAccent={`${parseMonthsToNameFromDate(
            new Date(startDate)
          )} - ${parseMonthsToNameFromDate(new Date(endDate))}`}
          headerAdditional={
            <span
              onClick={() => {
                refetch();
              }}
              className={sharedStyles['action']}
            >
              <span>Export</span>
            </span>
          }
        >
          <ErrorBoundary>
            <Suspense fallback={<SkeletonTable countTableBodyRows={20} />}>
              <TableReports
                startDate={startDate}
                endDate={endDate}
                projectId={project?.value}
                slug={slug}
              />
            </Suspense>
          </ErrorBoundary>
        </Card>
      </div>
    </>
  );
};

export default TabNewSingleMember;
